import { useState } from "react";
import { Check, Copy, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { montarMensagemDaAnamnese } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { useConfiguracaoDePacientes } from "@/lib/use-configuracao-de-pacientes";
import { env } from "@/lib/env";
import { AJUDA_PACIENTES } from "@/lib/ajuda/dialogos/pacientes";

const whatsapp = (telefone: string | null, texto: string) => {
  const d = (telefone ?? "").replace(/\D/g, "");
  return `https://wa.me/${d ? (d.startsWith("55") ? d : `55${d}`) : ""}?text=${encodeURIComponent(texto)}`;
};

/**
 * Gera o link da ficha de saúde e oferece copiar, WhatsApp e e-mail. Sem QR: o QR do convite de
 * cadastro é montado num serviço de terceiros, e o token de um link de saúde não sai daqui. A
 * mensagem não leva nenhum dado de saúde. O link aparece uma vez só (o banco guarda só o hash).
 */
export function EnviarAnamneseDialog({
  aberto,
  onFechar,
  paciente,
}: {
  aberto: boolean;
  onFechar: () => void;
  paciente: { id: string; name: string; phone: string | null; email: string | null; birthDate: string | Date | null };
}) {
  const utils = trpc.useUtils();
  // Validade e texto de Configurações › Pacientes (sem nada salvo, os de sempre: 7 dias e o texto padrão).
  const { validadeDoLinkDias, mensagem: modelo } = useConfiguracaoDePacientes().anamnese;
  const clinica = useAuth().user?.activeOrganization?.name ?? "";
  const [link, setLink] = useState<{ url: string; expiraEm: Date } | null>(null);
  const [copiado, setCopiado] = useState(false);
  const enviar = trpc.anamnese.enviar.useMutation({
    onSuccess: (r) => {
      setLink({ url: `${env.landingUrl}/a/${r.token}`, expiraEm: new Date(r.expiraEm) });
      utils.anamnese.listar.invalidate({ patientId: paciente.id });
    },
    onError: (e) => toast.error(e.message),
  });
  const fechar = () => {
    onFechar();
    setTimeout(() => {
      setLink(null);
      setCopiado(false);
    }, 200);
  };
  const mensagem = link
    ? montarMensagemDaAnamnese(modelo, {
        nome: paciente.name.split(" ")[0] ?? "",
        clinica,
        validade: link.expiraEm.toLocaleDateString("pt-BR"),
        link: link.url,
      })
    : "";
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(link!.url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && fechar()}>
      <DialogContent className="max-w-md border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_PACIENTES.enviarAnamnese}>Enviar ficha de saúde</DialogTitle>
          <DialogDescription>
            O paciente responde pelo celular, sem criar conta. O link abre com a data de nascimento e vale {validadeDoLinkDias === 1 ? "1 dia" : `${validadeDoLinkDias} dias`}.
          </DialogDescription>
        </DialogHeader>
        {!paciente.birthDate ? (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            Cadastre a data de nascimento de {paciente.name} na aba Dados: o link pede essa data para abrir.
          </p>
        ) : !link ? (
          <Button onClick={() => enviar.mutate({ patientId: paciente.id })} disabled={enviar.isPending} className="w-full">
            {enviar.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
            Gerar link
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="select-all break-all rounded-lg bg-zinc-50 p-3 font-mono text-[12px] text-zinc-800 dark:bg-muted dark:text-foreground">{link.url}</div>
            <p className="text-[11.5px] text-zinc-500">Este link aparece só agora. Se perder, gere outro — o anterior deixa de valer.</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <Button variant="outline" onClick={copiar}>
                {copiado ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
                {copiado ? "Copiado" : "Copiar"}
              </Button>
              <Button variant="outline" asChild>
                <a href={whatsapp(paciente.phone, mensagem)} target="_blank" rel="noreferrer">
                  WhatsApp
                </a>
              </Button>
              <Button variant="outline" asChild disabled={!paciente.email}>
                <a href={paciente.email ? `mailto:${paciente.email}?subject=${encodeURIComponent("Sua ficha de saúde")}&body=${encodeURIComponent(mensagem)}` : undefined}>
                  <Mail className="mr-1.5 h-4 w-4" />
                  E-mail
                </a>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
